/*
  agnello's live bits: café open/closed status (New Jersey time) and the homepage status line.
  Hours are written in the theme editor, one line per day range, e.g.
    Mon–Thu: 8am–3pm
    Fri–Sat: 8am–4pm
    Sun: closed
*/
(() => {
  const TZ = 'America/New_York';
  const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // "8am", "8:30am", "15:00", "3pm" -> minutes after midnight
  const parseTime = (s) => {
    const m = s.trim().toLowerCase().match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2] || '0', 10);
    if (m[3] === 'pm' && h < 12) h += 12;
    if (m[3] === 'am' && h === 12) h = 0;
    return h * 60 + min;
  };

  // Returns 7 entries (Sun..Sat), each null (closed) or [open, close] in minutes.
  const parseHours = (text) => {
    const week = Array(7).fill(null);
    for (const raw of text.split(/\n/)) {
      const line = raw.trim().toLowerCase().replace(/[–—]/g, '-');
      const split = line.indexOf(': ');
      if (split < 0) continue;
      const days = line.slice(0, split).replace(/\s/g, '');
      const value = line.slice(split + 2);
      const range = days.split('-').map((d) => DAYS.indexOf(d.slice(0, 3)));
      if (range.some((i) => i < 0)) continue;
      const span = [];
      for (let i = range[0]; ; i = (i + 1) % 7) { span.push(i); if (i === (range[1] ?? range[0])) break; }
      const times = value.split('-');
      const hours = /closed/.test(value) || times.length !== 2 ? null : [parseTime(times[0]), parseTime(times[1])];
      for (const i of span) week[i] = hours && hours[0] != null && hours[1] != null ? hours : null;
    }
    return week;
  };

  const now = () => {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: TZ, weekday: 'short', hour: 'numeric', minute: 'numeric', hour12: false })
      .formatToParts(new Date()).map((p) => [p.type, p.value]));
    return { day: DAY_NAMES.indexOf(parts.weekday), minutes: (parseInt(parts.hour, 10) % 24) * 60 + parseInt(parts.minute, 10) };
  };

  const fmt = (minutes) => {
    const h = Math.floor(minutes / 60), m = minutes % 60, suffix = h >= 12 ? 'pm' : 'am';
    return `${((h + 11) % 12) + 1}${m ? ':' + String(m).padStart(2, '0') : ''}${suffix}`;
  };

  // { open: bool, text: "Open until 3pm" | "Opens 8am" | "Opens Thu 8am", soon: bool }
  const status = (week, t = now()) => {
    const today = week[t.day];
    if (today && t.minutes >= today[0] && t.minutes < today[1]) {
      return { open: true, soon: today[1] - t.minutes <= 30, text: `Open until ${fmt(today[1])}` };
    }
    if (today && t.minutes < today[0]) return { open: false, text: `Opens ${fmt(today[0])}` };
    for (let i = 1; i <= 7; i++) {
      const d = (t.day + i) % 7;
      if (week[d]) return { open: false, text: `Opens ${i === 1 ? 'tomorrow' : DAY_NAMES[d]} ${fmt(week[d][0])}` };
    }
    return null;
  };

  // No open days at all: show the hours' own wording, e.g. "Mon–Sun: closed for the season" -> "Closed for the season".
  const closedStatus = (text) => {
    const m = text.toLowerCase().match(/closed[^\n]*/);
    return m ? { open: false, text: m[0].charAt(0).toUpperCase() + m[0].slice(1).trim() } : null;
  };

  const update = () => {
    const t = now();
    const cafes = [...document.querySelectorAll('[data-ag-cafe]')];
    const summary = [];
    for (const cafe of cafes) {
      const source = cafe.querySelector('[data-ag-hours]');
      if (!source) continue;
      const week = parseHours(source.textContent);
      const s = status(week, t) || (week.every((d) => !d) ? closedStatus(source.textContent) : null);
      const pill = cafe.querySelector('[data-ag-status]');
      cafe.querySelectorAll('[data-ag-day]').forEach((row) => {
        const days = row.dataset.agDay.toLowerCase().replace(/[–—]/g, '-').replace(/\s/g, '').split('-').map((d) => DAYS.indexOf(d.slice(0, 3)));
        let match = false;
        for (let i = days[0]; ; i = (i + 1) % 7) { if (i === t.day) match = true; if (i === (days[1] ?? days[0])) break; }
        row.classList.toggle('is-today', match);
      });
      if (pill && s) {
        pill.textContent = s.text;
        pill.dataset.state = s.open ? (s.soon ? 'soon' : 'open') : 'closed';
        pill.hidden = false;
      }
      if (s && !summary.some((c) => c.name === cafe.dataset.agCafe)) summary.push({ name: cafe.dataset.agCafe, ...s });
    }
    const line = document.querySelector('[data-ag-hero-status]');
    if (line && summary.length) {
      const open = summary.filter((c) => c.open);
      // Fewer, more useful facts (Hick's law): open cafés first; otherwise next openings, leaving out seasonal closures.
      const upcoming = summary.filter((c) => !/^closed/i.test(c.text));
      const parts = (open.length ? open : upcoming.length ? upcoming : summary).slice(0, 2).map((c) => `${c.name} ${c.text.charAt(0).toLowerCase()}${c.text.slice(1)}`);
      line.querySelector('[data-ag-hero-status-text]').textContent = parts.join(' · ');
      line.dataset.state = open.length ? 'open' : 'closed';
      line.hidden = false;
    }
  };

  update();
  setInterval(update, 60 * 1000);
  document.addEventListener('shopify:section:load', update);

  // Phones: sticky Add to cart bar once the product page's own button scrolls away. It presses the real button.
  // The theme re-renders the product form after load, so look the button up each time instead of keeping a reference.
  const findMainButton = () => document.querySelector('.product-detail product-form button[type="submit"]');
  if (document.querySelector('[data-ag-sticky-buy]') && findMainButton()) {
    let queued = false;
    const check = () => {
      queued = false;
      const sticky = document.querySelector('[data-ag-sticky-buy]');
      const button = findMainButton();
      if (!sticky || !button) return;
      sticky.hidden = false;
      // Shown only after scrolling past the real button (not before reaching it); works for fast flings too.
      sticky.classList.toggle('is-shown', button.getBoundingClientRect().bottom < 0);
      const price = sticky.querySelector('[data-ag-sticky-price]');
      const pagePrice = document.querySelector('.product-detail .price .theme-money, .product-detail .current-price');
      if (pagePrice && price) price.textContent = pagePrice.textContent.trim();
    };
    addEventListener('scroll', () => { if (!queued) { queued = true; requestAnimationFrame(check); } }, { passive: true });
    check();
    document.addEventListener('click', (evt) => {
      if (evt.target.closest('[data-ag-sticky-add]')) findMainButton()?.click();
    });
  }

  // Dialogs (cart drawer, quick buy, filters). Mode toggles .cc-popup--visible but leaves aria-hidden="true" on the
  // open dialog and the page behind it reachable. Mirror the class to ARIA, make everything else inert (which also
  // keeps Tab inside the dialog), move focus in, and give it back on close (WCAG 2.2 4.1.2, 2.4.3).
  let dialogOpener = null;
  let dialogWasOpen = false;
  const syncDialogs = () => {
    const all = [...document.querySelectorAll('.cc-popup')];
    const open = all.filter((p) => p.classList.contains('cc-popup--visible'));
    all.forEach((p) => p.setAttribute('aria-hidden', open.includes(p) ? 'false' : 'true'));
    const anyOpen = open.length > 0;
    if (anyOpen && !dialogWasOpen) dialogOpener = document.activeElement;
    [...document.body.children].forEach((el) => {
      if (['SCRIPT', 'STYLE', 'TEMPLATE', 'LINK'].includes(el.tagName)) return;
      el.inert = anyOpen && !open.some((p) => el === p || el.contains(p));
    });
    if (anyOpen) {
      const dialog = open[open.length - 1];
      const focusIn = () => {
        if (!dialog.classList.contains('cc-popup--visible') || dialog.contains(document.activeElement)) return;
        const target = dialog.querySelector('.cc-popup-close') || dialog.querySelector('[role="dialog"]');
        if (!target) return;
        if (target.tagName !== 'BUTTON' && !target.hasAttribute('tabindex')) target.tabIndex = -1;
        target.focus({ preventScroll: true });
      };
      // The dialog fades in over 0.25s and the cart re-renders after it opens, so try now and again shortly after.
      focusIn();
      [400, 900, 1600].forEach((ms) => setTimeout(focusIn, ms));
    } else if (dialogWasOpen) {
      // Mode restores focus before the class change lands, while the opener was still inert; do it again now.
      if (dialogOpener && dialogOpener.isConnected && (document.activeElement === document.body || !document.activeElement)) {
        dialogOpener.focus({ preventScroll: true });
      }
      dialogOpener = null;
    }
    dialogWasOpen = anyOpen;
  };
  new MutationObserver((records) => {
    if (records.some((r) => r.target.classList && r.target.classList.contains('cc-popup'))) syncDialogs();
  }).observe(document.body, { attributes: true, attributeFilter: ['class'], subtree: true });
  syncDialogs();

  // Shared-element page transitions: a product card's image morphs into the product page's main image.
  window.addEventListener('pageswap', (e) => {
    if (!e.viewTransition) return;
    const target = e.activation && e.activation.entry && e.activation.entry.url;
    if (!target) return;
    const img = document.querySelector(`[data-ag-vt-url="${new URL(target).pathname}"]`);
    if (img) img.style.viewTransitionName = 'ag-product';
  });
})();
